import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { ClaimBadge, Reveal } from "@/components/site/Primitives";
import { GLANCE, TIMELINE, PROOF, AUDIENCES, MARQUEE } from "@/lib/content";

const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-12"];

export const AtAGlance = () => (
  <div data-testid="at-a-glance" className="grid gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-12">
    {GLANCE.map((g, i) => (
      <Reveal key={g.key} delay={i * 0.05} className={`group relative min-w-0 bg-[#0B1D4F] p-6 transition-colors duration-500 hover:bg-[#12286A] md:p-9 ${SPANS[i]}`}>
        <div data-testid={`glance-${g.key}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300">{g.label}</span>
            <ClaimBadge type={g.type} testId={`glance-${g.key}-badge`} />
          </div>
          <p className={`font-display mt-6 font-extrabold uppercase leading-[0.9] break-words ${i < 2 ? "text-5xl lg:text-7xl" : "text-4xl lg:text-5xl"}`}>{g.value}</p>
          <p className="mt-4 max-w-xl text-sm text-slate-300 md:text-base">{g.detail}</p>
          <span className="absolute left-0 top-0 h-px w-0 bg-[#C8102E] transition-all duration-700 group-hover:w-full" />
        </div>
      </Reveal>
    ))}
  </div>
);

export const StatusTimeline = () => (
  <ol data-testid="status-timeline" className="relative border-l border-white/15 md:ml-4">
    {TIMELINE.map((t, i) => (
      <Reveal as="li" key={t.title} delay={i * 0.06} className="relative pb-12 pl-8 last:pb-0 md:pl-14">
        <span className={`absolute -left-[7px] top-1.5 h-3.5 w-3.5 rotate-45 ${t.type === "projected" ? "border border-amber-400 bg-[#061235]" : "bg-indigo-300"}`} />
        <div data-testid={`timeline-item-${i}`} className="grid gap-4 md:grid-cols-12">
          <p className="font-mono text-sm text-slate-200 md:col-span-3">{t.date}</p>
          <div className="md:col-span-9">
            <ClaimBadge type={t.type} testId={`timeline-badge-${i}`} />
            <h3 className="font-display mt-3 text-2xl font-bold uppercase sm:text-3xl">{t.title}</h3>
            <p className="mt-2 max-w-2xl text-slate-300">{t.body}</p>
            {t.url && <a href={t.url} target="_blank" rel="noreferrer" data-testid={`timeline-read-more-${i}`} aria-label={`Read more: ${t.title}`} className="group mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E3BC6E] underline-offset-4 hover:underline">Read more <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></a>}
          </div>
        </div>
      </Reveal>
    ))}
  </ol>
);

export const AudienceBand = () => (
  <div data-testid="audience-band" className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
    {AUDIENCES.map((a, i) => (
      <Link key={a.key} to={a.to} data-testid={`audience-cta-${a.key}`} className="group relative flex min-h-[260px] flex-col justify-between bg-[#0B1D4F] p-7 transition-colors duration-500 hover:bg-[#C8102E] hover:text-white">
        <span className="font-mono text-xs text-slate-300 group-hover:text-white">0{i + 1}</span>
        <div>
          <h3 className="font-display text-3xl font-bold uppercase leading-none group-hover:text-white">{a.title}</h3>
          <p className="mt-3 text-sm text-slate-300 group-hover:text-white">{a.body}</p>
          <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold">
            {a.cta} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </div>
      </Link>
    ))}
  </div>
);

export const ProofGrid = () => (
  <div data-testid="proof-grid" className="grid gap-6 md:grid-cols-2">
    {PROOF.map((p, i) => (
      <Reveal key={p.label} delay={i * 0.06} className="hairline chamfer-sm bg-[#0B1D4F] p-7 md:p-9">
        <div data-testid={`proof-item-${i}`}>
          <ClaimBadge type={p.type} testId={`proof-badge-${i}`} />
          <p className="font-display mt-5 text-6xl font-black text-white lg:text-7xl">{p.stat}</p>
          <p className="mt-2 font-semibold text-slate-100">{p.label}</p>
          <p className="mt-3 text-sm text-slate-300">{p.body}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const Marquee = () => {
  const row = [...MARQUEE, ...MARQUEE];
  return (
    <div data-testid="editorial-marquee" className="overflow-hidden border-y border-white/10 py-6" aria-hidden="true">
      <div className="marquee-track flex w-max gap-12 whitespace-nowrap">
        {[...row, ...row].map((m, i) => (
          <span key={i} className="font-display flex items-center gap-12 text-4xl font-bold uppercase text-white/70 md:text-6xl">
            {m}<Star className="h-6 w-6 fill-[#D4A24C] text-[#D4A24C]" />
          </span>
        ))}
      </div>
    </div>
  );
};

export const NewsList = ({ items }) => (
  <ul data-testid="news-list" className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
    {items.map((n, i) => (
      <li key={n.url} className="bg-[#0B1D4F]">
        <a href={n.url} target="_blank" rel="noreferrer" data-testid={`news-item-${i}`} className="group flex h-full flex-col justify-between gap-4 p-6 transition-colors duration-300 hover:bg-[#12286A]">
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#D4A24C]">{n.outlet}</span>
          <span className="font-display text-2xl font-bold uppercase leading-tight">{n.title}</span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-200">Read on {n.outlet} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </a>
      </li>
    ))}
  </ul>
);

export const Frame = ({ src, alt, caption, className = "" }) => (
  <figure className={className}>
    <div className="chamfer relative h-full overflow-hidden">
      <img src={src} alt={alt} className="h-full w-full object-cover transition-transform duration-[1.6s] ease-out hover:scale-105" loading="lazy" />
    </div>
    {caption && <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-400">{caption}</figcaption>}
  </figure>
);
